import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Trophy, Target, Brain, Heart, CheckCircle, Calendar, ArrowRight } from 'lucide-react';
import CTA from '../components/CTA';

const PerformanceAnxiety: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Performance Anxiety Hypnotherapy | Sports, Events & Exams</title>
        <meta name="description" content="Performance anxiety for NW200, golf tournaments, exams, sports, public speaking? Hypnotherapy helps you perform under pressure with mental preparation and visualization. Serving Portrush, Ballycastle & Northern Ireland." />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/performance-anxiety/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with sports performance anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is highly effective for sports performance anxiety. It helps athletes manage pre-competition nerves, improve focus and concentration, build confidence under pressure, and develop mental rehearsal techniques for peak performance. Many professional athletes use hypnotherapy as part of their training regimen."
                }
              },
              {
                "@type": "Question",
                "name": "How many sessions do I need for exam stress?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Most clients benefit from 3-5 sessions for exam anxiety, ideally starting 4-6 weeks before the exam. This allows time to build calm mental patterns, practice visualization techniques, and develop confidence in recall and performance. For ongoing exam stress (multiple exams or courses), a longer program may be beneficial."
                }
              },
              {
                "@type": "Question",
                "name": "Do you work with athletes and competitive performers?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely. Hypnotherapy supports athletes, musicians, public speakers, and anyone performing under pressure. Whether you're preparing for the NW200 motorcycle race, golf tournaments at Royal Portrush, fishing competitions in Ballycastle, or professional presentations, mental preparation through hypnotherapy can enhance your performance and reduce anxiety."
                }
              },
              {
                "@type": "Question",
                "name": "How does hypnotherapy improve performance under pressure?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Hypnotherapy helps regulate your nervous system so anxiety doesn't interfere with performance. It teaches your brain to stay calm and focused under pressure, improves visualization and mental rehearsal skills, builds confidence in your abilities, and helps you enter a 'flow state' more easily. This mental preparation translates to better real-world performance."
                }
              },
              {
                "@type": "Question",
                "name": "Can you help with public speaking anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is very effective for public speaking anxiety. It helps reduce the fear of judgment, calms physical symptoms (shaking, sweating, voice trembling), improves confidence in your message, and builds mental rehearsal skills so you feel prepared and calm when presenting. Sessions focus on both the anxiety reduction and performance enhancement."
                }
              },
              {
                "@type": "Question",
                "name": "What about work presentation anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Hypnotherapy addresses work presentation anxiety by helping you feel more confident and prepared. It reduces the catastrophic thinking ('I'll forget everything', 'They'll think I'm incompetent'), calms your nervous system during presentations, and builds mental rehearsal skills so you can visualize successful delivery before the actual event."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with competitive event nerves?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, whether it's the NW200 motorcycle race in Portrush, fishing competitions in Ballycastle, golf tournaments, or any competitive event, hypnotherapy helps manage pre-event anxiety and improves performance under pressure. Mental preparation sessions focus on building calm confidence and visualization techniques specific to your event."
                }
              },
              {
                "@type": "Question",
                "name": "How does visualization work in hypnotherapy?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Visualization in hypnosis is more powerful than regular mental imagery because your subconscious mind is more receptive. During hypnosis, you mentally rehearse your performance in vivid detail—seeing, hearing, and feeling successful execution. This creates neural pathways as if you've actually practiced, building familiarity and confidence for the real event."
                }
              },
              {
                "@type": "Question",
                "name": "Is hypnotherapy safe for performance enhancement?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is completely safe when practiced by a qualified clinical hypnotherapist. It's a natural mental training technique used by athletes, performers, and professionals worldwide. You remain in control throughout, and hypnotherapy complements (not replaces) physical training, practice, and skill development."
                }
              },
              {
                "@type": "Question",
                "name": "Can I use hypnotherapy alongside other performance coaching?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely. Hypnotherapy works well alongside sports coaching, music instruction, professional development, or any other performance training. It addresses the mental and emotional aspects of performance that complement the technical skills you're learning elsewhere. Many athletes and performers use hypnotherapy as part of a comprehensive training program."
                }
              }
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Performance Anxiety Hypnotherapy",
            "name": "Performance Anxiety & Mental Preparation Hypnotherapy",
            "description": "Professional solution-focused hypnotherapy for sports performance anxiety, exam stress, public speaking nerves, and competitive event preparation. Mental coaching for peak performance under pressure. Serving Portrush, Ballycastle and across Northern Ireland.",
            "provider": {
              "@type": "MedicalBusiness",
              "name": "Sharon Clare Hypnotherapy",
              "telephone": "07932139245",
              "email": "Sharon@sharonclare-hypnotherapy.com",
              "url": "https://www.sharonclare-hypnotherapy.com/"
            },
            "areaServed": ["Portrush", "Ballycastle", "Belfast", "Lisburn", "Northern Ireland"]
          })}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="container-custom text-center max-w-4xl mx-auto">
          <div className="flex justify-center mb-4">
            <Trophy className="w-12 h-12 text-blue-600" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-4">Performance Anxiety</h1>
          <p className="text-xl font-heading text-gray-900 mb-3">Perform at Your Peak When It Matters Most</p>
          <p className="text-lg sm:text-xl text-gray-600 mb-6">Sports anxiety, exam stress, public speaking nerves, competitive event pressure? Hypnotherapy helps you stay calm, focused, and confident when performing under pressure—from the NW200 to exam halls, boardrooms to golf courses.</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/book" className="btn-primary inline-flex items-center justify-center">
              <Calendar className="w-5 h-5 mr-2" /> Book Free 10-Minute Call
            </a>
            <a href="tel:07932139245" className="btn-secondary inline-flex items-center justify-center">
              <ArrowRight className="w-5 h-5 mr-2" /> Call 07932 139245
            </a>
          </div>
        </div>
      </section>

      {/* Types of Performance Anxiety */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">When Pressure Undermines Performance</h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            You've trained, practiced, prepared—but when the moment arrives, anxiety takes over. Your mind goes blank, your body tenses, and you can't access the skills you know you have. Performance anxiety doesn't mean you're unprepared; it means your nervous system is working against you when you need it most.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-3 flex items-center">
                <Trophy className="w-5 h-5 text-blue-600 mr-2" />
                Sports & Athletic Performance
              </h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>NW200 motorcycle race nerves (riders, crew, officials)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Golf tournament anxiety (Open Championship, club competitions)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Fishing competition pressure (Ballycastle, coastal events)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Team sports performance anxiety</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Fear of failure or choking under pressure</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-3 flex items-center">
                <Target className="w-5 h-5 text-purple-600 mr-2" />
                Academic & Exam Performance
              </h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Exam anxiety and test-taking stress</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Mind going blank during exams</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Driving test nerves</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Professional certification exam stress</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Academic presentation anxiety</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-3 flex items-center">
                <Brain className="w-5 h-5 text-indigo-600 mr-2" />
                Professional & Work Performance
              </h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Public speaking and presentation anxiety</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Job interview nerves</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Performance reviews and appraisals</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Client pitches and sales presentations</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Fear of public failure or judgment</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-3 flex items-center">
                <Heart className="w-5 h-5 text-rose-600 mr-2" />
                Competitive & Creative Performance
              </h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Musical performance anxiety (concerts, auditions)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Theatre and acting nerves</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Art exhibition or portfolio review anxiety</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Tourism industry event hosting (Portrush events, Bushmills distillery tours)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Any high-stakes creative or competitive situation</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-6">
            <p className="text-gray-700">
              <strong className="text-gray-900">Northern Ireland context:</strong> From golf tournament anxiety at Royal Portrush to competitive sporting events across the province, Northern Ireland hosts major competitive events that create unique performance pressure. Mental preparation through hypnotherapy helps performers, athletes, and professionals stay calm when it matters most.
            </p>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">How Hypnotherapy Enhances Performance</h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            Solution-focused hypnotherapy doesn't just reduce anxiety—it actively enhances your ability to perform under pressure. By working with your subconscious mind, hypnotherapy helps you access your full capabilities when stakes are high, stay calm in the moment, and build the mental resilience that separates good performers from great ones.
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center space-x-3 mb-3">
                <Brain className="w-6 h-6 text-blue-600" />
                <h3 className="font-heading font-semibold text-gray-900">Mental Rehearsal</h3>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                Visualization in hypnosis is more powerful than regular mental imagery. Your subconscious mind experiences the successful performance in vivid detail—creating neural pathways as if you've actually practiced. This builds familiarity, confidence, and automatic responses for the real event.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center space-x-3 mb-3">
                <Heart className="w-6 h-6 text-rose-600" />
                <h3 className="font-heading font-semibold text-gray-900">Calm Under Pressure</h3>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                Hypnotherapy helps regulate your autonomic nervous system so anxiety doesn't interfere with performance. You learn to stay physiologically calm (steady heart rate, relaxed muscles, clear thinking) even in high-pressure situations, allowing your skills to shine through.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center space-x-3 mb-3">
                <Target className="w-6 h-6 text-purple-600" />
                <h3 className="font-heading font-semibold text-gray-900">Build Confidence</h3>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                Replace self-doubt and catastrophic thinking with genuine self-belief. Hypnotherapy helps you trust your preparation, accept that mistakes are learning (not failure), and approach performance situations with an "I can handle this" mindset rather than fear.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
            <h3 className="font-heading font-semibold text-gray-900 mb-4">The Performance Preparation Process</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Pre-Event Preparation (Weeks Before)</h4>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Identify specific performance triggers (past failures, fear of judgment, physical symptoms)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Practice detailed mental rehearsal in hypnosis—see, hear, feel successful performance</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Build calm physiological responses to performance cues</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Develop pre-performance routines to trigger calm focus</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Day-of-Event Tools</h4>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Quick self-hypnosis techniques for instant calm</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Grounding exercises to manage nerves before performing</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Mental triggers to access "flow state" during performance</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Recovery techniques if anxiety arises mid-performance</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sharon's Approach */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">Sharon's Approach to Performance Enhancement</h2>
          <div className="grid md:grid-cols-2 gap-8 items-start">
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                As a solution-focused clinical hypnotherapist, Sharon works with performers, athletes, students, and professionals to build the mental resilience needed for peak performance. Hypnotherapy doesn't replace technical skill or practice—it ensures your mental state supports, rather than undermines, your abilities.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Sessions focus on two key areas: reducing the anxiety that interferes with performance (the mental "choke" response), and actively enhancing performance through visualization, confidence-building, and mental preparation techniques. This dual approach means you're not just less anxious—you're genuinely performing better.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Starting 4-6 weeks before your event, exam, or competition allows time for mental patterns to establish. Whether you're preparing for the NW200, an important exam, a work presentation, or any high-stakes performance situation, hypnotherapy provides the mental edge that complements your physical and technical preparation.
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6">
              <h3 className="font-heading font-semibold text-gray-900 mb-3">Why Mental Preparation Matters</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Access Your Full Abilities:</strong>
                    <p className="text-gray-700 text-sm">Anxiety limits access to skills you've trained. Mental preparation ensures you can use what you know when it matters most.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Build Competitive Edge:</strong>
                    <p className="text-gray-700 text-sm">When skill levels are equal, mental resilience separates winners from the rest. Hypnotherapy provides that edge.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Create Lasting Confidence:</strong>
                    <p className="text-gray-700 text-sm">Not just for one event—mental preparation builds resilience for all future high-pressure situations in your life.</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Causeway Coast Context */}
      <section className="py-16 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">Performance Support on the Causeway Coast</h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            The Causeway Coast hosts major competitive events throughout the year—from motorsport to golf tournaments, fishing competitions to tourism industry performances. Mental preparation through hypnotherapy helps athletes, performers, and professionals excel under pressure.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <a href="/portrush-hypnotherapy" className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Portrush</h3>
              <p className="text-sm text-gray-700 mb-3">
                <strong>Events capital:</strong> NW200 motorcycle race performance anxiety (riders, crew, officials), Open Championship golf tournament nerves, Royal Portrush club competition stress. Major event mental preparation essential. Online sessions available.
              </p>
              <span className="text-blue-600 text-sm font-medium">Learn more →</span>
            </a>

            <a href="/ballycastle-hypnotherapy" className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Ballycastle</h3>
              <p className="text-sm text-gray-700 mb-3">
                <strong>Coastal working town:</strong> Fishing competition anxiety, ferry crew performance under pressure, Rathlin boat crossing operational stress. Working-class practicality meets competitive performance. Online sessions available (18 mi).
              </p>
              <span className="text-blue-600 text-sm font-medium">Learn more →</span>
            </a>
          </div>

          <div className="mt-6 bg-white rounded-xl p-6 shadow-lg border border-gray-100">
            <h3 className="font-heading font-semibold text-gray-900 mb-3">Clinic &amp; Online Sessions</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              Sharon sees clients in person at her <strong>Belfast clinic on the Ormeau Road</strong> and at <strong>The Arzen Collective in Lisburn</strong>, with early evening appointments available for athletes and performers training around work.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Online sessions also provide maximum flexibility for athletes and performers with unpredictable schedules. Schedule sessions around training, competitions, or events. No travel time means more energy for performance preparation and practice.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-8">Frequently Asked Questions</h2>
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Can hypnotherapy help with sports performance anxiety?</h3>
              <p className="text-gray-700 leading-relaxed">
                Yes, hypnotherapy is highly effective for sports performance anxiety. It helps athletes manage pre-competition nerves, improve focus and concentration, build confidence under pressure, and develop mental rehearsal techniques for peak performance. Many professional athletes use hypnotherapy as part of their training regimen.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">How many sessions do I need for exam stress?</h3>
              <p className="text-gray-700 leading-relaxed">
                Most clients benefit from 3-5 sessions for exam anxiety, ideally starting 4-6 weeks before the exam. This allows time to build calm mental patterns, practice visualization techniques, and develop confidence in recall and performance. For ongoing exam stress (multiple exams or courses), a longer program may be beneficial.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Do you work with athletes and competitive performers?</h3>
              <p className="text-gray-700 leading-relaxed">
                Absolutely. Hypnotherapy supports athletes, musicians, public speakers, and anyone performing under pressure. Whether you're preparing for the NW200 motorcycle race, golf tournaments at Royal Portrush, fishing competitions in Ballycastle, or professional presentations, mental preparation through hypnotherapy can enhance your performance and reduce anxiety.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">How does hypnotherapy improve performance under pressure?</h3>
              <p className="text-gray-700 leading-relaxed">
                Hypnotherapy helps regulate your nervous system so anxiety doesn't interfere with performance. It teaches your brain to stay calm and focused under pressure, improves visualization and mental rehearsal skills, builds confidence in your abilities, and helps you enter a 'flow state' more easily. This mental preparation translates to better real-world performance.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Can you help with public speaking anxiety?</h3>
              <p className="text-gray-700 leading-relaxed">
                Yes, hypnotherapy is very effective for public speaking anxiety. It helps reduce the fear of judgment, calms physical symptoms (shaking, sweating, voice trembling), improves confidence in your message, and builds mental rehearsal skills so you feel prepared and calm when presenting.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Is hypnotherapy safe for performance enhancement?</h3>
              <p className="text-gray-700 leading-relaxed">
                Yes, hypnotherapy is completely safe when practiced by a qualified clinical hypnotherapist. It's a natural mental training technique used by athletes, performers, and professionals worldwide. You remain in control throughout, and hypnotherapy complements (not replaces) physical training, practice, and skill development.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">Related Services</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <a href="/anxiety-stress" className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">General Anxiety & Stress</h3>
              <p className="text-sm text-gray-700 mb-3">
                Broader anxiety treatment including social anxiety, work stress, and panic attacks.
              </p>
              <span className="text-blue-600 text-sm font-medium">Learn more →</span>
            </a>

            <a href="/holiday-anxiety" className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Holiday & Travel Anxiety</h3>
              <p className="text-sm text-gray-700 mb-3">
                Pre-trip anxiety, fear of flying, ferry stress, and vacation overwhelm preparation.
              </p>
              <span className="text-blue-600 text-sm font-medium">Learn more →</span>
            </a>

            <a href="/medical-procedure-anxiety" className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Medical Procedure Anxiety</h3>
              <p className="text-sm text-gray-700 mb-3">
                Mental preparation for medical procedures, scans, dental work, and healthcare appointments.
              </p>
              <span className="text-blue-600 text-sm font-medium">Learn more →</span>
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTA
        title="Unlock Your Peak Performance Potential"
        subtitle="Don't let anxiety undermine your abilities. Mental preparation through hypnotherapy helps you perform at your best when it matters most—whether in sports, exams, presentations, or any competitive situation."
        primaryButtonText="Book Free 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default PerformanceAnxiety;
